import React, { useCallback, useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import Icon from '../../../utils/Icon';
import Button from '../../Button';
import TextField from '../TextField';
import styles from './SearchField.scss';
import useT from '../../../utils/Translations/useT';
import useLayoutDirection from '../../../utils/Translations/useLayoutDirection';
import useHelpNoteTitle from '../../../utils/Translations/useHelpNoteTitle';
const DEBOUNCE_INTERVAL = 2000;
const SearchField = ({ onChange, onReset, widthClassName = 'col-lg-4 col-md-4 col-sm-6 col-xs-12', infoMessage = '', isInTable = false, rightActionComponent = null, refFunc, disabled = false, inputClassName = '', value = '', isDebouncingEnabled = true, hasInternalQueryState = true, hasResetButton = true, inputProps = {}, className = '', searchIconClasses = '', removeIconClasses = '', placeholder = '', hasBottomMargin = true, searchProps = { isChangeRaisedOnBlur: true }, dataCy, isRtlPullRightEnabled = true, }) => {
    const t = useT();
    const { isLayoutRTL } = useLayoutDirection();
    const searchTitle = placeholder || t('Search');
    const searchPlaceholder = placeholder || `${searchTitle}...`;
    const searchTooltipTitle = useHelpNoteTitle(searchTitle);
    const removeButton = useRef(null);
    const [query, setQuery] = useState(value);
    const onDebouncedQueryChange = useCallback(onChange, [onChange]);
    const handleDebouncedQueryChange = useCallback(_.debounce(onDebouncedQueryChange, DEBOUNCE_INTERVAL), []);
    useEffect(() => () => handleDebouncedQueryChange.cancel(), [
        handleDebouncedQueryChange,
    ]);
    const onChangeCall = useCallback(query => {
        if (isDebouncingEnabled) {
            handleDebouncedQueryChange.cancel();
        }
        onChange(query);
    }, [onChange, isDebouncingEnabled, handleDebouncedQueryChange]);
    const handleUpdateQuery = useCallback((immediate, query) => {
        if (query === ' ') {
            return;
        }
        const newQuery = _.trimStart(query);
        const outerCall = !isDebouncingEnabled || immediate
            ? onChangeCall
            : handleDebouncedQueryChange;
        outerCall(newQuery);
        hasInternalQueryState && setQuery(newQuery);
    }, [
        isDebouncingEnabled,
        hasInternalQueryState,
        setQuery,
        handleDebouncedQueryChange,
        onChangeCall,
    ]);
    const handleChange = useCallback(({ target: { value } }) => handleUpdateQuery(false, value), [handleUpdateQuery]);
    const handleBlur = useCallback(e => {
        if (removeButton.current && removeButton.current !== e.relatedTarget) {
            handleUpdateQuery(true, e.target.value);
        }
    }, [handleUpdateQuery]);
    const handleEnter = useCallback(({ target: { value } }) => handleUpdateQuery(true, value), [handleUpdateQuery]);
    const handleReset = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        onReset ? onReset() : handleUpdateQuery(true, '');
    }, [onReset, handleUpdateQuery]);
    const searchQuery = isDebouncingEnabled && hasInternalQueryState ? query : value;
    const setRemoveButton = useCallback(e => (removeButton.current = e), []);
    return (<div className={classnames({ row: !isInTable })}>
      <div className={classnames(widthClassName, {
        'pull-right': isLayoutRTL && isRtlPullRightEnabled,
    })}>
        <div className={classnames('has-feedback form-group', {
        'mb-0': !hasBottomMargin,
    })}>
          <TextField {...searchProps} className={className} disabled={disabled} formElement={false} infoMessage={infoMessage} inputProps={{
        ...inputProps,
        className: classnames(styles.inputClassName, inputClassName),
        'data-cy': dataCy,
    }} name="search" placeholder={searchPlaceholder} refFunc={refFunc} tooltipTitle={searchTooltipTitle} type="text" value={searchQuery} onBlur={handleBlur} onChange={handleChange} onEnter={handleEnter}/>
          {hasResetButton && searchQuery && (<div className={classnames(styles.removeIconContainer, {
        [styles.removeIconContainerWithRightButton]: rightActionComponent,
    })} onTouchEnd={handleReset}>
              <Button additionClasses={removeIconClasses} buttonStyle="link" forwardRef={setRemoveButton} padding="no-padding" position="right" size="xs" onClick={handleReset}>
                <Icon className={classnames(styles.removeIcon, 'remove-icon')} name="cross3"/>
              </Button>
            </div>)}
          {rightActionComponent ? (<div className={styles.rightButton}>{rightActionComponent}</div>) : (<div className={classnames('form-control-feedback', styles.searchIconContainer, searchIconClasses)}>
              <Icon className={classnames(styles.searchIcon, 'search-icon')} name="search"/>
            </div>)}
        </div>
      </div>
    </div>);
};
export default SearchField;
